import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex h-[22px] items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground',
        secondary: 'bg-secondary text-secondary-foreground',
        destructive: 'bg-destructive text-destructive-foreground',
        outline: 'border border-border text-foreground',
        dotted: 'border border-border uppercase tracking-wider text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /**
   * Render as the child element (shadcn `asChild`) instead of a `<span>` — e.g.
   * wrap a link or button so the badge inherits its behaviour and focus ring.
   * The child must be a single element; the `dotted` leading dot is only
   * injected for the default span, so supply your own marker when composing.
   */
  asChild?: boolean;
}

const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';
    return (
      <Comp
        ref={ref}
        className={cn(badgeVariants({ variant, className }))}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {variant === 'dotted' && (
              <span
                aria-hidden
                className="size-[7px] shrink-0 rounded-full bg-primary"
              />
            )}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Badge.displayName = 'Badge';

export { Badge, badgeVariants };
